import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Bell, MonitorSmartphone, MessageCircle, Loader2 } from 'lucide-react';
import { enablePush, disablePush, pushSupported } from '@/lib/push';
import { useTranslation } from 'react-i18next';

export type NotificationPrefs = {
  email_enabled: boolean;
  inapp_enabled: boolean;
  push_enabled: boolean;
  whatsapp_enabled: boolean;
};

const DEFAULTS: NotificationPrefs = { email_enabled: true, inapp_enabled: true, push_enabled: true, whatsapp_enabled: false };

const Row: React.FC<{ icon: React.ReactNode; title: string; desc: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }> = ({ icon, title, desc, checked, disabled, onChange }) => (
  <div className="flex items-center justify-between border rounded-lg px-4 py-3">
    <div className="flex items-start gap-3 min-w-0">
      <div className="text-muted-foreground mt-0.5">{icon}</div>
      <div className="min-w-0">
        <Label className="text-sm font-medium">{title}</Label>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </div>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
  </div>
);

/**
 * Reusable notification-channel opt-in/out card (in-app / email / browser-push / WhatsApp).
 * Loads + saves to user_notification_prefs for the signed-in user. Browser push registers /
 * unregisters the subscription via @/lib/push. Only writes the channel columns on upsert, so
 * it never clobbers the user's saved `theme` (managed by AppearanceCard).
 */
const NotificationSettingsCard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user?.id) { setLoading(false); return; }
      const { data } = await supabase
        .from('user_notification_prefs')
        .select('email_enabled, inapp_enabled, push_enabled, whatsapp_enabled')
        .eq('user_id', user.id).maybeSingle();
      if (data) setPrefs({ email_enabled: data.email_enabled, inapp_enabled: data.inapp_enabled, push_enabled: data.push_enabled, whatsapp_enabled: data.whatsapp_enabled });
      setLoading(false);
    })();
  }, [user?.id]);

  const update = useCallback(async (patch: Partial<NotificationPrefs>) => {
    if (!user?.id) return;
    const next = { ...prefs, ...patch };
    // Browser push: register/unregister the subscription. Revert the toggle if the
    // user denies permission or it isn't supported.
    if (patch.push_enabled !== undefined) {
      if (patch.push_enabled) {
        if (!pushSupported()) { toast.error(t('settings.pushNotSupported')); return; }
        const ok = await enablePush();
        if (!ok) { toast.error(t('settings.pushPermissionDenied')); return; }
      } else {
        await disablePush();
      }
    }
    setPrefs(next);
    setSaving(true);
    const { error } = await supabase.from('user_notification_prefs').upsert(
      { user_id: user.id, ...next, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' },
    );
    setSaving(false);
    if (error) { toast.error(t('settings.couldNotSave', { message: error.message })); return; }
    toast.success(t('settings.saved'));
  }, [prefs, user?.id, t]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {t('settings.notifications')}
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t('settings.notificationsCardDesc')}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading')}</div>
        ) : (
          <>
            <Row icon={<Bell className="w-4 h-4" />} title={t('settings.chInapp')} desc={t('settings.chInappDesc')} checked={prefs.inapp_enabled} onChange={(v) => update({ inapp_enabled: v })} />
            <Row icon={<Mail className="w-4 h-4" />} title={t('settings.chEmail')} desc={t('settings.chEmailDesc')} checked={prefs.email_enabled} onChange={(v) => update({ email_enabled: v })} />
            <Row icon={<MonitorSmartphone className="w-4 h-4" />} title={t('settings.chPush')} desc={t('settings.chPushDesc')} checked={prefs.push_enabled} onChange={(v) => update({ push_enabled: v })} />
            <Row icon={<MessageCircle className="w-4 h-4" />} title={t('settings.chWhatsapp')} desc={t('settings.chWhatsappDesc')} checked={prefs.whatsapp_enabled} onChange={(v) => update({ whatsapp_enabled: v })} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettingsCard;
